import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Confetti from 'react-confetti';
import { LabelMedium } from './Typography';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { isAdmin } from '../utils/roleUtils';
import Notepad from './Notepad';
import ThemeToggle from './ThemeToggle';

const StudentSidebar = ({ 
  onNavigateToHome, 
  onNavigateToCourses, 
  onNavigateToProfile, 
  onNavigateToPrompts, 
  onNavigateToWritersRoom, 
  onLogOut, 
  showAdminMode = false, 
  onModeSwitch 
}) => {
  const { userRole } = useAuth();
  const { themeColors } = useTheme();
  const location = useLocation();
  
  // Consistent sidebar menu text color - update here to change all menu items
  // Use textPrimary in dark mode for better contrast, textSecondary in light mode
  const sidebarMenuTextColor = themeColors.isDark ? themeColors.textPrimary : themeColors.textSecondary;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Collapse state - persist in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Writing Tools state
  const [isTimerOpen, setIsTimerOpen] = useState(false);
  const [timerMinutes, setTimerMinutes] = useState(5);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerIntervalRef = useRef(null);
  const timerRef = useRef(null);
  const timerButtonRef = useRef(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Audio player state
  const [isAudioOpen, setIsAudioOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState('ambient.mp3');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const audioRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const audioButtonRef = useRef(null);

  // Notepad state
  const [isNotepadOpen, setIsNotepadOpen] = useState(false);
  const notepadButtonRef = useRef(null);

  const audioOptions = [
    { value: 'ambient.mp3', label: 'Ambient' },
    { value: 'jazz.mp3', label: 'Jazz' },
    { value: 'classical.mp3', label: 'Classical' },
    { value: 'lofi.mp3', label: 'Lo-Fi' }
  ];

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
    // Dispatch custom event so StudentLayout can update
    window.dispatchEvent(new Event('sidebarStateChanged'));
  }, [isCollapsed]);

  // Update window dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Timer countdown logic
  useEffect(() => {
    if (isTimerRunning && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            /* Skip confetti when the OS prefers reduced motion (WCAG 2.3.3) */
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (!reducedMotion) {
              setShowConfetti(true);
              setTimeout(() => {
                setShowConfetti(false);
              }, 5000); // Show confetti for 5 seconds
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isTimerRunning, timeRemaining]);

  // Initialize audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.loop = true;
    }
  }, [volume]);

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      
      // Check if click is on timer button - if so, skip (let toggle handle it)
      const isClickOnTimerButton = timerButtonRef.current && timerButtonRef.current.contains(event.target);
      // Check if click is on audio button - if so, skip (let toggle handle it)
      const isClickOnAudioButton = audioButtonRef.current && audioButtonRef.current.contains(event.target);
      // Check if click is on notepad button - if so, skip (let toggle handle it)
      const isClickOnNotepadButton = notepadButtonRef.current && notepadButtonRef.current.contains(event.target);
      
      // If clicking on the buttons themselves, don't handle click-outside logic
      if (isClickOnTimerButton || isClickOnAudioButton || isClickOnNotepadButton) {
        return;
      }
      
      // Check if click is outside timer
      if (timerRef.current && !timerRef.current.contains(event.target)) {
        // Don't close timer if it's running
        if (!isTimerRunning) {
          setIsTimerOpen(false);
        }
      }
      
      // Check if click is outside audio player
      if (audioPlayerRef.current && !audioPlayerRef.current.contains(event.target)) {
        setIsAudioOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimerRunning]);

  // Determine active route
  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    if (path === '/courses') {
      return location.pathname === '/courses' || location.pathname.startsWith('/courses/');
    }
    if (path === '/prompts') {
      return location.pathname === '/prompts' || location.pathname.startsWith('/prompts/');
    }
    if (path === '/writers-room') {
      return location.pathname === '/writers-room';
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    if (onNavigateToProfile) {
      onNavigateToProfile();
    }
  };

  const handleLogOutClick = () => {
    setIsDropdownOpen(false);
    if (onLogOut) {
      onLogOut();
    }
  };

  const handleModeSwitch = () => {
    setIsDropdownOpen(false);
    if (onModeSwitch) {
      onModeSwitch();
    }
  };


  // Timer functions
  const toggleTimer = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsTimerOpen(!isTimerOpen);
    if (!isTimerOpen) {
      setIsAudioOpen(false);
    }
  };

  const adjustTime = (delta) => {
    const newMinutes = Math.max(1, Math.min(60, timerMinutes + delta));
    setTimerMinutes(newMinutes);
    if (!isTimerRunning) {
      setTimeRemaining(newMinutes * 60);
    }
  };

  const toggleTimerRunning = () => {
    if (!isTimerRunning && timeRemaining === 0) {
      setTimeRemaining(timerMinutes * 60);
    }
    setIsTimerRunning(!isTimerRunning);
  };

  const resetTimer = () => {
    setIsTimerRunning(false);
    setTimeRemaining(timerMinutes * 60);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerProgress = () => {
    const totalSeconds = timerMinutes * 60;
    return ((totalSeconds - timeRemaining) / totalSeconds) * 100;
  };

  // Audio player functions
  const toggleAudioPlayer = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsAudioOpen(!isAudioOpen);
    if (!isAudioOpen) {
      if (!isTimerRunning) setIsTimerOpen(false);
    }
  };

  // Notepad functions
  const toggleNotepad = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsNotepadOpen(!isNotepadOpen);
    if (!isNotepadOpen) {
      setIsTimerOpen(false);
      setIsAudioOpen(false);
    }
  };

  const handleAudioSelect = async (audio) => {
    setSelectedAudio(audio);
    if (isAudioPlaying && audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = `/images/writers-room/audio/${audio}`;
        audioRef.current.load();
        await audioRef.current.play();
      } catch (error) {
        console.error('Error switching audio:', error);
        setIsAudioPlaying(false);
      }
    }
  };

  const toggleAudioPlayback = async () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
        setIsAudioPlaying(false);
      } else {
        try {
          await audioRef.current.play();
          setIsAudioPlaying(true);
        } catch (error) {
          console.error('Error playing audio:', error);
          setIsAudioPlaying(false);
        }
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const navigationItems = [
    ...(onNavigateToHome ? [{
      id: 'home',
      label: 'Home',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      action: onNavigateToHome
    }] : []),
    {
      id: 'courses',
      label: 'Courses',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      action: onNavigateToCourses
    },
    {
      id: 'prompts',
      label: 'Prompts',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      action: onNavigateToPrompts
    },
    {
      id: 'writers-room',
      label: 'Writers Room',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      action: onNavigateToWritersRoom
    }
  ];

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';

  return (
    <>
      {/* Confetti Animation */}
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
          initialVelocityY={20}
          initialVelocityX={10}
          colors={['#9333ea', '#06b6d4', '#3B82F6', '#60A5FA', '#DBEAFE', '#1F2937', '#374151', '#6B7280']}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        />
      )}

      <div 
        className={`${sidebarWidth} h-screen flex flex-col fixed left-0 top-0 z-50 transition-all duration-300 ease-in-out border-r`} 
        style={{ backgroundColor: themeColors.sidebar, borderColor: themeColors.border, color: themeColors.textPrimary }}
      >
      {/* Storykin Brand & Toggle Button */}
      <div className={`${isCollapsed ? 'p-4' : 'p-6'} border-b flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`} style={{ borderColor: themeColors.divider }}>
        {!isCollapsed && (
          <div className="flex items-center space-x-3 flex-shrink-0">
            <img 
              src="/images/storykin_favicon.png" 
              alt="STORYKIN" 
              className="h-10 w-10 object-contain transition-opacity duration-300"
            />
          </div>
        )}
        {isCollapsed && (
          <button
            onClick={toggleCollapse}
            className="flex items-center justify-center hover:opacity-80 transition-opacity duration-200 cursor-pointer flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 rounded"
            aria-label="Expand sidebar"
          >
            <img 
              src="/images/storykin_favicon.png" 
              alt="STORYKIN" 
              className="h-10 w-10 object-contain transition-opacity duration-300"
            />
          </button>
        )}
        {!isCollapsed && (
          <button
            onClick={toggleCollapse}
            className="p-1.5 rounded transition-colors duration-200 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            aria-label="Collapse sidebar"
          >
            <svg 
              className="w-4 h-4 transition-opacity duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" stroke="currentColor" fill="none" />
              <line x1="9" y1="3" x2="9" y2="21" strokeWidth="2" stroke="currentColor" />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-6 overflow-y-auto">
        <ul className={`space-y-2 ${isCollapsed ? 'px-2' : 'px-4'}`}>
          {navigationItems.map((item) => {
            let active = false;
            if (item.id === 'home') {
              active = isActive('/home');
            } else if (item.id === 'writers-room') {
              active = isActive('/writers-room');
            } else {
              active = isActive(`/${item.id}`);
            }
            
            return (
              <li key={item.id}>
                <button
                  onClick={item.action}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1`}
                  style={active ? { backgroundColor: '#0A4872' } : {}}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = themeColors.borderSubtle;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="flex-shrink-0" style={{ color: active ? '#ffffff' : sidebarMenuTextColor }}>
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <LabelMedium 
                      className="transition-opacity duration-300 opacity-100"
                      style={{ color: active ? '#ffffff' : sidebarMenuTextColor }}
                    >
                      {item.label}
                    </LabelMedium>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Writing Tools Section */}
      {!isCollapsed && (
        <div className="border-t py-4" style={{ borderColor: themeColors.divider }}>
          <div className={`px-4 mb-3`}>
            <LabelMedium className="text-xs uppercase tracking-wider" style={{ color: sidebarMenuTextColor }}>
              Writing Tools
            </LabelMedium>
          </div>
          <ul className={`space-y-2 ${isCollapsed ? 'px-2' : 'px-4'}`}>
            {/* Timer */}
            <li>
              <button
                ref={timerButtonRef}
                onClick={toggleTimer}
                onMouseDown={(e) => e.stopPropagation()}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors duration-200`}
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isCollapsed ? 'Timer' : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {!isCollapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <LabelMedium className="transition-opacity duration-300 opacity-100" style={{ color: sidebarMenuTextColor }}>
                      Timer
                    </LabelMedium>
                    {isTimerRunning && (
                      <span className="text-xs font-mono" style={{ color: themeColors.textTertiary }}>
                        {formatTime(timeRemaining)}
                      </span>
                    )}
                  </div>
                )}
              </button>
            </li>
            {/* Music Player */}
            <li>
              <button
                ref={audioButtonRef}
                onClick={toggleAudioPlayer}
                onMouseDown={(e) => e.stopPropagation()}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors duration-200`}
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isCollapsed ? 'Music' : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                {!isCollapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <LabelMedium className="transition-opacity duration-300 opacity-100" style={{ color: sidebarMenuTextColor }}>
                      Music
                    </LabelMedium>
                    {isAudioPlaying && (
                      <div className="flex items-center space-x-2">
                        <div className="flex items-end space-x-0.5 h-3">
                          <div className="w-0.5 bg-purple-500 rounded-full animate-wave" style={{ animationDelay: '0s', height: '40%' }}></div>
                          <div className="w-0.5 bg-purple-500 rounded-full animate-wave" style={{ animationDelay: '0.1s', height: '70%' }}></div>
                          <div className="w-0.5 bg-purple-500 rounded-full animate-wave" style={{ animationDelay: '0.2s', height: '100%' }}></div>
                          <div className="w-0.5 bg-purple-500 rounded-full animate-wave" style={{ animationDelay: '0.3s', height: '60%' }}></div>
                        </div>
                        <span className="text-xs truncate ml-1 max-w-[100px]" style={{ color: themeColors.textTertiary }}>
                          {audioOptions.find(opt => opt.value === selectedAudio)?.label || selectedAudio}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </button>
            </li>
            {/* Notepad */}
            <li>
              <button
                ref={notepadButtonRef}
                onClick={toggleNotepad}
                onMouseDown={(e) => e.stopPropagation()}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors duration-200`}
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isCollapsed ? 'Notepad' : undefined}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {!isCollapsed && (
                  <LabelMedium className="transition-opacity duration-300 opacity-100" style={{ color: sidebarMenuTextColor }}>
                    Notepad
                  </LabelMedium>
                )}
              </button>
            </li>
            {/* Theme Toggle */}
            <li>
              <ThemeToggle isCollapsed={isCollapsed} />
            </li>
          </ul>
        </div>
      )}

      {/* Writing Tools - Collapsed View (Icons Only) */}
      {isCollapsed && (
        <div className="border-t py-4" style={{ borderColor: themeColors.divider }}>
          <ul className="space-y-2 px-2">
            <li>
              <button
                ref={timerButtonRef}
                onClick={toggleTimer}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full flex items-center justify-center px-0 py-3 rounded-lg transition-colors duration-200 relative"
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isTimerRunning ? `Timer: ${formatTime(timeRemaining)}` : 'Timer'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {isTimerRunning && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                )}
              </button>
            </li>
            <li>
              <button
                ref={audioButtonRef}
                onClick={toggleAudioPlayer}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full flex items-center justify-center px-0 py-3 rounded-lg transition-colors duration-200 relative"
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isAudioPlaying ? `Music: ${audioOptions.find(opt => opt.value === selectedAudio)?.label || selectedAudio}` : 'Music'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                {isAudioPlaying && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                )}
              </button>
            </li>
            <li>
              <button
                ref={notepadButtonRef}
                onClick={toggleNotepad}
                onMouseDown={(e) => e.stopPropagation()}
                className="w-full flex items-center justify-center px-0 py-3 rounded-lg transition-colors duration-200 relative"
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title={isNotepadOpen ? 'Notepad' : 'Notepad'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: sidebarMenuTextColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {isNotepadOpen && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-600 rounded-full animate-pulse"></span>
                )}
              </button>
            </li>
            {/* Theme Toggle - Collapsed */}
            <li>
              <ThemeToggle isCollapsed={true} />
            </li>
          </ul>
        </div>
      )}

      {/* Footer with User Menu */}
      <div className={`${isCollapsed ? 'p-2' : 'p-4'} border-t`} style={{ borderColor: themeColors.divider }}>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-expanded={isDropdownOpen}
            aria-controls="sidebar-user-menu"
            className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors duration-200`}
            style={{ 
              color: sidebarMenuTextColor,
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title={isCollapsed ? 'Menu' : undefined}
            aria-label="User menu"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: themeColors.card, border: `1px solid ${themeColors.border}` }}>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" style={{ color: sidebarMenuTextColor }}>
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            {!isCollapsed && (
              <LabelMedium className="transition-opacity duration-300 opacity-100" style={{ color: sidebarMenuTextColor }}>
                Menu
              </LabelMedium>
            )}
          </button>

          {/* Dropdown Menu — disclosure widget; no role=menu so no arrow-key expectation */}
          {isDropdownOpen && (
            <div 
              id="sidebar-user-menu"
              className={`absolute bottom-full left-0 mb-2 rounded-lg shadow-xl py-2 z-50 transition-all duration-300 ${
                isCollapsed ? 'w-56' : 'w-full'
              }`}
              style={{ 
                left: isCollapsed ? '4rem' : '0',
                backgroundColor: themeColors.card,
                border: `1px solid ${themeColors.border}`
              }}
            >
              {/* Mode Switch - only show for admins */}
              {isAdmin(userRole) && (
                <button
                  onClick={handleModeSwitch}
                  className="w-full text-left px-4 py-3 transition-colors duration-200 flex items-center space-x-2"
                  style={{ 
                    color: sidebarMenuTextColor,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  <span>{showAdminMode ? 'Student Mode' : 'Admin Mode'}</span>
                </button>
              )}
              
              {/* Profile */}
              <button
                onClick={handleProfileClick}
                className="w-full text-left px-4 py-3 transition-colors duration-200 flex items-center space-x-2"
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Profile</span>
              </button>
              
              <button
                onClick={handleLogOutClick}
                className="w-full text-left px-4 py-3 transition-colors duration-200 flex items-center space-x-2"
                style={{ color: sidebarMenuTextColor }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeColors.borderSubtle}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Log Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={`/images/writers-room/audio/${selectedAudio}`}
        loop
      />

      {/* Notepad */}
      <Notepad isOpen={isNotepadOpen} onClose={() => setIsNotepadOpen(false)} />

      {/* Timer Modal - Positioned next to Writing Tools section */}
      {isTimerOpen && (
        <div
          className="fixed z-[60]"
          ref={timerRef}
          style={{ left: isCollapsed ? '4rem' : '16rem', top: 'calc(100vh - 500px)' }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setIsTimerOpen(false); timerButtonRef.current?.focus(); } }}
        >
          <div className="bg-white rounded-full w-60 h-60 flex flex-col items-center justify-center shadow-2xl relative">
            {/* Circular Progress Bar */}
            {isTimerRunning && (
              <svg className="absolute top-0 left-0 w-full h-full transform -rotate-90 pointer-events-none">
                <circle
                  cx="120"
                  cy="120"
                  r="112"
                  fill="none"
                  stroke="url(#timerGradient)"
                  strokeWidth="10"
                  strokeDasharray={`${2 * Math.PI * 112}`}
                  strokeDashoffset={`${2 * Math.PI * 112 * (1 - getTimerProgress() / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
                <defs>
                  <linearGradient id="timerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#0A4872" />
                    <stop offset="100%" stopColor="#0A4872" />
                  </linearGradient>
                </defs>
              </svg>
            )}

            {/* Time Adjustment Controls */}
            <div className="flex items-center space-x-3 mb-4 relative z-10">
              {/* min-h/min-w expand the tap target to 44px without changing the visual circle size */}
              <button
                onClick={() => adjustTime(-1)}
                disabled={isTimerRunning}
                aria-label="Decrease timer by 1 minute"
                className="min-h-[44px] min-w-[44px] rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg text-gray-800"
              >
                −
              </button>
              <span className="text-xs font-medium text-gray-800">1 min</span>
              <button
                onClick={() => adjustTime(1)}
                disabled={isTimerRunning}
                aria-label="Increase timer by 1 minute"
                className="min-h-[44px] min-w-[44px] rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-bold text-lg text-gray-800"
              >
                +
              </button>
            </div>

            {/* Timer Display */}
            <div className="text-5xl font-bold mb-4 relative z-10 text-gray-900" style={{ fontFamily: 'monospace' }}>
              {formatTime(timeRemaining)}
            </div>

            {/* Control Buttons */}
            <div className="flex items-center space-x-3 relative z-10">
              {/* Play/Pause Button */}
              <button
                onClick={toggleTimerRunning}
                aria-label={isTimerRunning ? 'Pause timer' : 'Start timer'}
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors"
                style={{ backgroundColor: '#0A4872' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#083d5f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0A4872'}
              >
                {isTimerRunning ? (
                  <svg aria-hidden="true" className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Reset Button */}
              <button
                onClick={resetTimer}
                aria-label="Reset timer"
                className="w-12 h-12 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center shadow-lg transition-colors"
              >
                <svg aria-hidden="true" className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Player Modal - Positioned below Timer */}
      {isAudioOpen && (
        <div
          className="fixed z-[60]"
          ref={audioPlayerRef}
          style={{
            left: isCollapsed ? '4rem' : '16rem',
            top: isTimerOpen
              ? 'calc(100vh - 500px + 260px)'
              : 'calc(100vh - 500px)'
          }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setIsAudioOpen(false); audioButtonRef.current?.focus(); } }}
        >
          <div className="bg-white rounded-2xl px-6 py-5 shadow-2xl w-80">
            {/* Audio Selection Dropdown and Play Button */}
            <div className="flex items-center space-x-3 mb-4">
              <select
                value={selectedAudio}
                onChange={(e) => handleAudioSelect(e.target.value)}
                aria-label="Select background audio track"
                className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm text-gray-900"
              >
                {audioOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {/* Play/Pause Button */}
              <button
                onClick={toggleAudioPlayback}
                aria-label={isAudioPlaying ? 'Pause background audio' : 'Play background audio'}
                className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center shadow-lg transition-colors"
                style={{ backgroundColor: '#0A4872' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#083d5f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0A4872'}
              >
                {isAudioPlaying ? (
                  <svg aria-hidden="true" className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
              </svg>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #0A4872 0%, #0A4872 ${volume * 100}%, #e5e7eb ${volume * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default StudentSidebar;
